import { useMemo } from "react";
import { Button, Checkbox, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import CalendarFilter from "../Notifications/CalendarFilter";
import { useHistoryFilter } from "../../hooks/useHistoryFilter";
import { groupHistoryByDate } from "./historyUtils";
import type { ChatMessage } from "../Chat/chat";
import AskVivoHistoryRow from "./AskVivoHistoryRow";
import { stripContextPrefix } from "./askVivo.utils";
import AskVivoDeleteConfirmModal from "./AskVivoDeleteConfirmModal";
import { useDontAskAgain } from "@/utils/useDontAskAgain";

interface AskVivoHistoryPanelProps {
  isLoading: boolean;
  error: string | null;
  historyData: ChatMessage[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  compact?: boolean;
  bulkDelete?: {
    showCheckboxes: boolean;
    checkedList: string[];
    confirmingBulk: boolean;
    setShowCheckboxes: (value: boolean) => void;
    setCheckedList: (value: string[]) => void;
    setConfirmingBulk: (value: boolean) => void;
    onDeleteMultiple: (sessionIds: string[]) => void | Promise<void>;
  };
}

function HistoryContent({
  isLoading,
  error,
  historyData,
  filtered,
  groupKeys,
  grouped,
  activeSessionId,
  onSelect,
  onDelete,
}: {
  isLoading: boolean;
  error: string | null;
  historyData: ChatMessage[];
  filtered: ChatMessage[];
  groupKeys: string[];
  grouped: Record<string, ChatMessage[]>;
  activeSessionId: string | null;
  onSelect: (s: string) => void;
  onDelete: (s: string) => void;
}) {
  if (isLoading) {
    return <p className="text-sm text-[#556C75]">Loading history…</p>;
  }
  if (error) {
    return <p className="text-sm text-[#C0392B]">{error}</p>;
  }
  if (historyData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-[#556C75]">
        <p className="text-sm">No past sessions yet.</p>
        <p className="text-xs mt-1">
          Your chats will appear here once you start one.
        </p>
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <p className="text-sm text-[#556C75] mt-3">No chats match your search.</p>
    );
  }
  return (
    <>
      {groupKeys.map((dateKey, dateIndex) => (
        <div key={dateKey} className="mb-3">
          <p
            className="text-[#556C75] text-xs mb-1 mt-2 uppercase tracking-wide"
            data-testid={`history-date-heading-${dateIndex}`}
          >
            {dateKey}
          </p>
          {grouped[dateKey].map((item) => (
            <AskVivoHistoryRow
              key={item.session_id}
              item={item}
              isActive={activeSessionId === item.session_id}
              onSelect={onSelect}
              onDelete={onDelete}
              rowTestId={
                item.session_id
                  ? `chat-history-item-${item.session_id}`
                  : undefined
              }
              textTestId={
                item.session_id
                  ? `history-item-text-${item.session_id}`
                  : undefined
              }
            />
          ))}
        </div>
      ))}
    </>
  );
}

export default function AskVivoHistoryPanel({
  isLoading,
  error,
  historyData,
  activeSessionId,
  onSelect,
  onDelete,
  compact = false,
  bulkDelete,
}: AskVivoHistoryPanelProps) {
  const { query, setQuery, calendarFilter, filtered } =
    useHistoryFilter(historyData);
  const { dontAskAgain, setDontAskAgain } = useDontAskAgain(
    "askVivo.skipDeleteConfirm",
  );
  const showCheckboxes = bulkDelete?.showCheckboxes ?? false;
  const checkedList = bulkDelete?.checkedList ?? [];
  const confirmingBulk = bulkDelete?.confirmingBulk ?? false;

  const grouped = useMemo(() => groupHistoryByDate(filtered), [filtered]);
  const groupKeys = Object.keys(grouped);

  const handleSelectAll = (e: { target: { checked: boolean } }) => {
    if (!bulkDelete) return;

    const nextChecked = e.target.checked
      ? filtered
          .map((item) => item.session_id)
          .filter((id): id is string => id !== undefined)
      : [];
    bulkDelete.setCheckedList(nextChecked);
  };

  const handleToggleItem = (sessionId: string, checked: boolean) => {
    if (!bulkDelete) return;

    const nextChecked = checked
      ? [...checkedList, sessionId]
      : checkedList.filter((id) => id !== sessionId);
    bulkDelete.setCheckedList(nextChecked);
  };

  const handleDeleteSelected = () => {
    if (!bulkDelete) return;

    if (dontAskAgain) {
      bulkDelete.onDeleteMultiple(checkedList);
      bulkDelete.setShowCheckboxes(false);
      bulkDelete.setCheckedList([]);
      bulkDelete.setConfirmingBulk(false);
      return;
    }

    bulkDelete.setConfirmingBulk(true);
  };

  const handleConfirmBulkDelete = () => {
    if (!bulkDelete) return;

    bulkDelete.onDeleteMultiple(checkedList);
    bulkDelete.setShowCheckboxes(false);
    bulkDelete.setCheckedList([]);
    bulkDelete.setConfirmingBulk(false);
  };

  const handleCancelBulk = () => {
    if (!bulkDelete) return;

    bulkDelete.setShowCheckboxes(false);
    bulkDelete.setCheckedList([]);
    bulkDelete.setConfirmingBulk(false);
  };

  return (
    <div
      className={`flex flex-1 min-h-0 flex-col overflow-hidden ${
        compact ? "" : "px-4 py-3"
      }`}
      data-testid="history-panel"
    >
      {historyData.length > 0 && !showCheckboxes && (
        <div className="flex flex-wrap items-center gap-2 mb-3 shrink-0">
          <div className="flex-1 min-w-30">
            <Input
              allowClear
              size="middle"
              prefix={<SearchOutlined style={{ color: "#8097A0" }} />}
              placeholder="Search chats…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="chat-history-search-input"
            />
          </div>
          <div className="shrink-0">
            <CalendarFilter calendarFilter={calendarFilter} />
          </div>
        </div>
      )}

      {showCheckboxes ? (
        <>
          <Checkbox
            className="mb-2 mt-1 shrink-0"
            onChange={handleSelectAll}
            checked={
              checkedList.length > 0 &&
              checkedList.length ===
                filtered.filter((i) => i.session_id !== undefined).length
            }
            indeterminate={
              checkedList.length > 0 &&
              checkedList.length <
                filtered.filter((i) => i.session_id !== undefined).length
            }
            data-testid="select-all-checkbox"
          >
            Select all
          </Checkbox>
          <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
            {groupKeys.map((dateKey, dateIndex) => (
              <div key={dateKey} className="mb-3">
                <p
                  className="text-[#556C75] text-xs mb-1 mt-2 uppercase tracking-wide"
                  data-testid={`history-date-heading-${dateIndex}`}
                >
                  {dateKey}
                </p>
                {grouped[dateKey].map((item) => (
                  <Checkbox
                    key={item.session_id}
                    checked={
                      !!item.session_id && checkedList.includes(item.session_id)
                    }
                    className="mb-2 w-full"
                    onChange={(e) =>
                      item.session_id &&
                      handleToggleItem(item.session_id, e.target.checked)
                    }
                  >
                    <span className="text-[#2B4752] text-sm">
                      {stripContextPrefix(item.input ?? "")}
                    </span>
                  </Checkbox>
                ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3 shrink-0">
            <Button
              type="default"
              className="text-[#064C68]"
              onClick={handleCancelBulk}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="delete-selected border-0"
              disabled={checkedList.length === 0}
              data-testid="delete-selected-button"
              onClick={handleDeleteSelected}
            >
              Delete Selected
            </Button>
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto hide-scrollbar">
          <HistoryContent
            isLoading={isLoading}
            error={error}
            historyData={historyData}
            filtered={filtered}
            groupKeys={groupKeys}
            grouped={grouped}
            activeSessionId={activeSessionId}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        </div>
      )}
      <AskVivoDeleteConfirmModal
        open={confirmingBulk}
        onCancel={handleCancelBulk}
        onConfirm={handleConfirmBulkDelete}
        saveDontAskAgain={setDontAskAgain}
        count={checkedList.length}
      />
    </div>
  );
}
